import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { MAX_PROBLEM_COUNT } from '@/constant'
import { worksheetController } from '@/controller/worksheet.controller'

/**
 * @deprecated
 * BE와 소통하는 filter 제거하면서, 교재쪽에서 사용하지 않게 됨. 앞으로 학습지도 삭제하는 방향으로
 */
export class MakeLessonMaterialFilterService<
  U extends 필터아이디필터,
  R extends Record<string, any> = { problemCount: number },
  E extends { message?: string; type: string } = { message?: string; type: 'overflow' },
> {
  request: U
  response?: ({ status: 'success'; error?: never } & R) | { status: 'error'; error: E } // 추후 필요하면 errorType 외부에서 받을 수 있도록

  requestAsync?: (request: U) => Promise<string>
  private _responseAsync: (
    filterId: string,
  ) => Promise<MakeLessonMaterialFilterService<U, R, E>['response']>

  constructor(data: {
    request: U
    requestAsync?: MakeLessonMaterialFilterService<U, R, E>['requestAsync']
    responseAsync?: (
      filterId: string,
    ) => Promise<MakeLessonMaterialFilterService<U, R, E>['response']>
  }) {
    makeAutoObservable(this)
    this.request = data.request
    this.requestAsync = data.requestAsync
    this._responseAsync =
      data.responseAsync ??
      (async (filterId: string) => {
        try {
          const problemCount = await worksheetController.fetchProblemCountByFilterId(filterId)
          return { status: 'success', problemCount }
        } catch (e) {
          if (e instanceof AxiosError && e.code === 'ERR_CANCELED') {
            throw e
          }
          return {
            status: 'error',
            error: { type: 'overflow' } as E, // 왜 추론을 못할까..
          }
        }
      })
  }

  get isValid() {
    // 기존 출제 문제 제외를 '학생 기준'으로 골랐는데 대상 학생이 없으면 출제 불가
    const request = this.request as Partial<공통지원교재필터>
    if (
      request.excludePrevious &&
      request.previousExclusionScope === 'STUDENT_ASSIGNED' &&
      !request.studentIds?.length
    ) {
      return false
    }
    if (!this.response) return true
    if (this.response.status === 'error') {
      return false
    } else {
      return this.response.problemCount > 0 && this.response.problemCount <= MAX_PROBLEM_COUNT
    }
  }

  updateAsync = async (request: U, opt?: { skipResponse?: boolean }) => {
    if (!this.requestAsync) return
    const filterId = await this.requestAsync(request)
    this.request = { ...request, filterId }
    if (opt?.skipResponse) return
    const response = await this._responseAsync(filterId)
    this.response = response
  }
}

export type 필터아이디필터 = { filterId: string | null }
export type 문제수필터 = Pick<res.SearchFilterByChapterFilterId, 'problemCount'>
export type 난이도필터 = Pick<res.SearchFilterByChapterFilterId, 'level'>
type 난이도설정필터 = Pick<res.SearchFilterByChapterFilterId, 'levelWeight'>

export type 문제타입필터 = Pick<
  res.SearchFilterByChapterFilterId,
  'problemFilterType' | 'onlyAutoScorable'
>
export type 모의고사포함여부필터 = Pick<res.SearchFilterByChapterFilterId, 'practiceTest'>
export type 기존출제문제제외필터 = Pick<res.SearchFilterByChapterFilterId, 'excludePrevious'>
export type 기존출제문제제외기준필터 = Pick<
  req.CreateFilterByPublicWorkbook,
  'previousExclusionScope' | 'studentIds'
>
export type 교육과정외유형제외필터 = Pick<req.CommonCreateOrUpdateFilter, 'excludeOOC'>
type 문제수균등배분필터 = Pick<res.SearchFilterByChapterFilterId, 'equalityLevel'>
type 세부정답률필터 = Pick<res.SearchFilterByChapterFilterId, 'minRate' | 'maxRate'>
export type 유형별최대문제수제한필터 = Pick<
  req.CreateFilterByPublicWorkbook,
  'limitProblemCountPerConcept'
>
export type 쌍둥이문제배수필터 = Pick<req.CreateFilterByPublicWorkbook, 'pairX'>
export type 유사문제배수필터 = Pick<req.CreateFilterByPublicWorkbook, 'similarX'>
export type 유사문제난이도필터 = Pick<req.CreateFilterByPublicWorkbook, 'similarLevel'>
type 배점필터 = Pick<req.CreateFilterByPracticeTest, 'scoreList'>
export type 원본문제포함필터 = Pick<req.CreateFilterByPublicWorkbook, 'includeSameProblem'>

export type 공통지원교재필터 = 쌍둥이문제배수필터 &
  유사문제배수필터 &
  유사문제난이도필터 &
  모의고사포함여부필터 &
  문제타입필터 &
  기존출제문제제외필터 &
  기존출제문제제외기준필터 &
  교육과정외유형제외필터 &
  원본문제포함필터

type _지원교재필터 = 공통지원교재필터 & {
  workbookId: WorkbookEntity.Workbook['id']
  workbookProblemIdList: res.StudentWorkbookPageDetail['workbookPageId'][]
  exampleProblemIds: req.CreateFilterByPublicWorkbook['exampleProblemIds']
}

export module 학습지필터 {
  export type 단원유형별필터 = 필터아이디필터 &
    문제수필터 &
    난이도필터 &
    난이도설정필터 &
    문제타입필터 &
    모의고사포함여부필터 &
    기존출제문제제외필터 &
    교육과정외유형제외필터 &
    문제수균등배분필터 &
    세부정답률필터

  export type 지원교재필터 = 필터아이디필터 & _지원교재필터 & 유형별최대문제수제한필터
  export type 수능모의고사필터 = 필터아이디필터 &
    배점필터 &
    문제타입필터 &
    교육과정외유형제외필터 & {
      practiceProblemIdList: number[] // 모의고사 문제 범위 (문제 id[])
    }
  export type 학교별기출필터 = 필터아이디필터 &
    유형별최대문제수제한필터 &
    공통지원교재필터 &
    Pick<
      req.CreateFilterBySchoolTest,
      | 'conceptIdList'
      | 'schoolCodes'
      | 'schoolTestYears'
      | 'schoolTestType'
      | 'schoolTestSchoolType'
      | 'schoolTestGrade'
      | 'schoolTestSemester'
    >
  export type 병합학습지필터 = req.CreateFilterByMerge
  export type 나의DB원본필터 = req.CreateFilterByMyDbOriginal
  export type 나의DB유사필터 = req.CreateFilterByMyDbSimilar
}
